<template>
  <div class="money-input-wrapper">
    <label v-if="label" :for="inputId" class="money-input-label">
      {{ label }}
      <span v-if="required" class="required-indicator">*</span>
    </label>
    
    <div class="money-input-container" :class="containerClasses">
      <div class="currency-prefix">
        <span class="currency-symbol">{{ currencySymbol }}</span>
      </div>
      
      <input
        :id="inputId"
        ref="input"
        v-model="displayValue"
        type="text"
        class="money-input"
        :class="inputClasses"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :aria-label="ariaLabel || label"
        :aria-invalid="hasError"
        :aria-describedby="errorId"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
        @keydown="handleKeydown"
      />
      
      <div v-if="showCalculations" class="calculation-display">
        <transition name="fade">
          <div v-if="taxAmount > 0" class="tax-calculation">
            <span class="calc-label">Taxa:</span>
            <span class="calc-value">{{ formatCurrency(taxAmount) }}</span>
          </div>
        </transition>
        
        <transition name="fade">
          <div v-if="netAmount !== modelValue" class="net-calculation">
            <span class="calc-label">Líquido:</span>
            <span class="calc-value">{{ formatCurrency(netAmount) }}</span>
          </div>
        </transition>
      </div>
    </div>
    
    <transition name="slide-down">
      <div v-if="hasError && errorMessage" :id="errorId" class="error-message">
        <Icon name="mdi-alert-circle-outline" />
        {{ errorMessage }}
      </div>
    </transition>
    
    <transition name="slide-down">
      <div v-if="hint && !hasError" class="hint-message">
        <Icon name="mdi-information-outline" />
        {{ hint }}
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Icon from '@/components/base/Icon.vue';

export default {
  name: 'MoneyInput',
  
  components: {
    Icon
  },
  
  props: {
    modelValue: {
      type: Number,
      default: 0
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '0,00'
    },
    currency: {
      type: String,
      default: 'BRL'
    },
    locale: {
      type: String,
      default: 'pt-BR'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: null
    },
    step: {
      type: Number,
      default: 0.01
    },
    taxPercentage: {
      type: Number,
      default: 0
    },
    showCalculations: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    ariaLabel: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'outlined', 'filled'].includes(value)
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    }
  },
  
  emits: ['update:modelValue', 'input', 'blur', 'focus', 'enter'],
  
  setup(props, { emit }) {
    const input = ref(null);
    const isFocused = ref(false);
    const internalValue = ref(props.modelValue);
    
    // Generate unique IDs
    const inputId = computed(() => `money-input-${Math.random().toString(36).substr(2, 9)}`);
    const errorId = computed(() => `${inputId.value}-error`);
    
    // Currency configuration
    const currencyConfig = {
      BRL: { symbol: 'R$', decimal: ',', thousands: '.', precision: 2 },
      USD: { symbol: '$', decimal: '.', thousands: ',', precision: 2 },
      EUR: { symbol: '€', decimal: ',', thousands: '.', precision: 2 }
    };
    
    const config = computed(() => currencyConfig[props.currency] || currencyConfig.BRL);
    const currencySymbol = computed(() => config.value.symbol);
    
    // Computed values
    const taxAmount = computed(() => {
      if (!props.taxPercentage || !internalValue.value) return 0;
      return internalValue.value * (props.taxPercentage / 100);
    });
    
    const netAmount = computed(() => {
      return Math.max(0, internalValue.value - taxAmount.value);
    });
    
    const hasError = computed(() => {
      return !!props.error || (props.required && !internalValue.value) || 
             (props.min !== null && internalValue.value < props.min) ||
             (props.max !== null && internalValue.value > props.max);
    });
    
    const errorMessage = computed(() => {
      if (props.error) return props.error;
      if (props.required && !internalValue.value) return 'Este campo é obrigatório';
      if (props.min !== null && internalValue.value < props.min) {
        return `O valor mínimo é ${formatCurrency(props.min)}`;
      }
      if (props.max !== null && internalValue.value > props.max) {
        return `O valor máximo é ${formatCurrency(props.max)}`;
      }
      return '';
    });
    
    // Display value formatting
    const displayValue = computed({
      get() {
        if (!internalValue.value && !isFocused.value) return '';
        return formatNumber(internalValue.value);
      },
      set(value) {
        const parsed = parseNumber(value);
        if (!isNaN(parsed)) {
          internalValue.value = parsed;
        }
      }
    });
    
    // Classes
    const containerClasses = computed(() => ({
      'money-input-focused': isFocused.value,
      'money-input-disabled': props.disabled,
      'money-input-readonly': props.readonly,
      'money-input-error': hasError.value,
      [`money-input-${props.variant}`]: props.variant,
      [`money-input-${props.size}`]: props.size
    }));
    
    const inputClasses = computed(() => ({
      'has-value': internalValue.value > 0,
      'is-placeholder': !internalValue.value && !isFocused.value
    }));
    
    // Formatting functions
    function formatNumber(value) {
      if (!value) return '0,00';
      
      const fixed = Number(value).toFixed(config.value.precision);
      const parts = fixed.split('.');
      
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.value.thousands);
      
      return parts.join(config.value.decimal);
    }
    
    function formatCurrency(value) {
      return `${config.value.symbol} ${formatNumber(value)}`;
    }
    
    function parseNumber(value) {
      if (!value) return 0;
      
      // Remove currency symbol and spaces
      let cleaned = value.replace(config.value.symbol, '').trim();
      
      // Replace decimal separator with dot
      cleaned = cleaned.replace(config.value.thousands, '');
      cleaned = cleaned.replace(config.value.decimal, '.');
      
      // Remove any non-numeric characters except dot
      cleaned = cleaned.replace(/[^0-9.-]/g, '');
      
      return parseFloat(cleaned) || 0;
    }
    
    // Event handlers
    function handleInput(event) {
      const value = parseNumber(event.target.value);
      if (!isNaN(value)) {
        internalValue.value = value;
        emit('update:modelValue', value);
        emit('input', value);
      }
    }
    
    function handleBlur() {
      isFocused.value = false;
      
      // Apply constraints on blur
      if (props.min !== null && internalValue.value < props.min) {
        internalValue.value = props.min;
      }
      if (props.max !== null && internalValue.value > props.max) {
        internalValue.value = props.max;
      }
      
      // Format to fixed precision
      const rounded = Math.round(internalValue.value * 100) / 100;
      if (rounded !== internalValue.value) {
        internalValue.value = rounded;
      }
      
      emit('update:modelValue', internalValue.value);
      emit('blur', internalValue.value);
    }
    
    function handleFocus() {
      isFocused.value = true;
      emit('focus', internalValue.value);
      
      // Select all on focus for easy editing
      setTimeout(() => {
        input.value?.select();
      }, 0);
    }
    
    function handleKeydown(event) {
      // Handle arrow keys for increment/decrement
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        incrementValue();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        decrementValue();
      } else if (event.key === 'Enter') {
        emit('enter', internalValue.value);
      }
    }
    
    function incrementValue() {
      const newValue = internalValue.value + props.step;
      if (props.max === null || newValue <= props.max) {
        internalValue.value = newValue;
        emit('update:modelValue', newValue);
      }
    }
    
    function decrementValue() {
      const newValue = internalValue.value - props.step;
      if (props.min === null || newValue >= props.min) {
        internalValue.value = newValue;
        emit('update:modelValue', newValue);
      }
    }
    
    // Watch for external changes
    watch(() => props.modelValue, (newValue) => {
      if (newValue !== internalValue.value) {
        internalValue.value = newValue;
      }
    });
    
    // Keyboard shortcuts
    const handleGlobalKeydown = (event) => {
      if (event.ctrlKey && event.key === 'k' && document.activeElement === input.value) {
        event.preventDefault();
        internalValue.value = 1000;
        emit('update:modelValue', 1000);
      }
    };
    
    onMounted(() => {
      document.addEventListener('keydown', handleGlobalKeydown);
    });
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleGlobalKeydown);
    });
    
    return {
      input,
      inputId,
      errorId,
      displayValue,
      currencySymbol,
      isFocused,
      hasError,
      errorMessage,
      taxAmount,
      netAmount,
      containerClasses,
      inputClasses,
      formatCurrency,
      handleInput,
      handleBlur,
      handleFocus,
      handleKeydown
    };
  }
};
</script>

<style scoped>
.money-input-wrapper {
  width: 100%;
}

.money-input-label {
  display: block;
  margin-bottom: var(--spacing-form-labelGap);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  letter-spacing: var(--letter-spacing-wide);
}

.required-indicator {
  color: var(--color-status-danger);
  margin-left: 0.25rem;
}

.money-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-background-glass);
  border: 1px solid var(--color-border-primary);
  border-radius: var(--spacing-form-borderRadius);
  transition: var(--transition-all);
  overflow: hidden;
}

.money-input-container:hover:not(.money-input-disabled):not(.money-input-readonly) {
  border-color: var(--color-border-secondary);
  background: var(--color-background-glassHover);
}

.money-input-focused {
  border-color: var(--color-brand-primary) !important;
  box-shadow: var(--shadow-input-focus) !important;
  background: var(--color-background-glassHover) !important;
}

.money-input-error {
  border-color: var(--color-status-danger) !important;
}

.money-input-error.money-input-focused {
  box-shadow: var(--shadow-input-error) !important;
}

.money-input-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.money-input-readonly {
  background: var(--color-background-secondary);
}

/* Currency prefix */
.currency-prefix {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 var(--spacing-3);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-semibold);
  background: var(--color-background-glass);
  border-right: 1px solid var(--color-border-primary);
  z-index: 1;
}

/* Input field */
.money-input {
  flex: 1;
  width: 100%;
  padding: var(--spacing-form-inputPadding);
  padding-left: 3.5rem;
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  font-variant-numeric: tabular-nums;
  text-align: right;
  outline: none;
}

.money-input::placeholder {
  color: var(--color-text-tertiary);
  opacity: 0.6;
}

.money-input.is-placeholder {
  color: var(--color-text-tertiary);
}

/* Size variants */
.money-input-small .money-input {
  padding: var(--spacing-2) var(--spacing-3);
  padding-left: 3rem;
  font-size: var(--font-size-sm);
}

.money-input-large .money-input {
  padding: var(--spacing-4) var(--spacing-5);
  padding-left: 4rem;
  font-size: var(--font-size-lg);
}

/* Variant styles */
.money-input-outlined {
  background: transparent;
}

.money-input-filled {
  background: var(--color-background-elevated);
  border-color: transparent;
}

.money-input-filled:hover:not(.money-input-disabled) {
  background: var(--color-background-elevatedHover);
}

/* Calculation display */
.calculation-display {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  padding: 0 var(--spacing-3);
  border-left: 1px solid var(--color-border-primary);
  font-size: var(--font-size-sm);
}

.tax-calculation,
.net-calculation {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  white-space: nowrap;
}

.calc-label {
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);
}

.calc-value {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
}

.net-calculation .calc-value {
  color: var(--color-status-success);
}

/* Messages */
.error-message,
.hint-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  margin-top: var(--spacing-2);
  font-size: var(--font-size-sm);
}

.error-message {
  color: var(--color-status-danger);
}

.hint-message {
  color: var(--color-text-tertiary);
}

.error-message i,
.hint-message i {
  font-size: var(--font-size-base);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .money-input-container,
  .fade-enter-active,
  .fade-leave-active,
  .slide-down-enter-active,
  .slide-down-leave-active {
    transition: none !important;
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .currency-prefix {
    background: rgba(0, 0, 0, 0.3);
  }
}
</style>